/**
 * Phase 14.5 Step 2 — Geometry section (Phase 13).
 *
 * Sweep the engagement envelope. Reads the geometry-campaigns
 * registry (registry_version="13.0") for sampler strategies +
 * envelope dimensions. Total-runs readout enforces 5000-cap.
 *
 * Registry version mismatch (≠ "13.0") blocks submission via the
 * `registry_version_blocked` boolean returned in the section's
 * value (the wizard checks this and surfaces it as a blocking
 * error). This mirrors the Phase 13 Step 5 dialog convention.
 */
import { useEffect, useMemo } from "react";
import { Grid3x3 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TOTAL_RUNS_CAP = 5000;
const SUPPORTED_REGISTRY_VERSION = "13.0";

function _samplers(registry) {
    const arr = registry?.sampler_strategies;
    return Array.isArray(arr) ? arr : [];
}
function _envelopeDims(registry) {
    const arr = registry?.envelope_dimensions;
    return Array.isArray(arr) ? arr : [];
}
function _defaultEnvelope(registry) {
    const out = {};
    for (const dim of _envelopeDims(registry)) {
        const lo = dim?.physical_bounds?.lo ?? 0;
        const hi = dim?.physical_bounds?.hi ?? lo + 1;
        out[dim.id] = { lo, hi };
    }
    return out;
}

export default function GeometrySection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
    registry,
}) {
    const samplers = useMemo(() => _samplers(registry), [registry]);
    const envelopeDims = useMemo(() => _envelopeDims(registry), [registry]);
    const sampler = value?.sampler || samplers[0]?.kind || "uniform";
    const n_points = Number(value?.n_points ?? 50);
    const samples_per_point = Math.max(1, Number(value?.samples_per_point ?? 5));
    const envelope = value?.envelope || {};
    const registryVersion = registry?.registry_version || null;
    const registryVersionBlocked =
        registry !== null && registryVersion !== SUPPORTED_REGISTRY_VERSION;

    const setField = (k, v) => onChange?.({ ...(value || {}), [k]: v });
    const setEnvelope = (dimId, axis, raw) => {
        const next = {
            ...envelope,
            [dimId]: {
                ...(envelope[dimId] || {}),
                [axis]: parseFloat(raw),
            },
        };
        setField("envelope", next);
    };

    // Seed default envelope on first registry load
    useEffect(() => {
        if (!enabled || !registry) return;
        if (Object.keys(envelope).length > 0) return;
        const seed = _defaultEnvelope(registry);
        if (Object.keys(seed).length > 0) {
            setField("envelope", seed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, registry]);

    // Phase 14.9 W2 — total trajectories readout addresses PM "varied
    // NUMBER of trajectories" ask. n_points × samples_per_point is the
    // honest total: the operator dials per-cell trajectory count to
    // sample engagement-pose variance within each envelope cell.
    const totalRuns = Math.max(0, n_points * samples_per_point);
    const overCap = totalRuns > TOTAL_RUNS_CAP;

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-geometry"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-geometry"
                    aria-label="Enable geometry sweep"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Grid3x3 className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">Geometry envelope sweep</span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Sample the operational envelope (range / bearing / altitude / approach)
                        with Uniform, Stratified, LHS, or Grid samplers.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-4" data-testid="geometry-details">
                    {registryVersionBlocked && (
                        <div
                            className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200"
                            data-testid="geometry-registry-version-error"
                        >
                            Registry version <code>{registryVersion}</code> is not supported by
                            this wizard build (expected <code>{SUPPORTED_REGISTRY_VERSION}</code>).
                            Submission blocked.
                        </div>
                    )}
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            Sampler
                        </Label>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Sampler">
                            {samplers.map((s) => (
                                <button
                                    key={s.kind}
                                    type="button"
                                    role="radio"
                                    aria-checked={sampler === s.kind}
                                    data-testid={`geometry-sampler-${s.kind}`}
                                    onClick={() => setField("sampler", s.kind)}
                                    className={[
                                        "rounded-full border px-3 py-1 text-xs transition-colors",
                                        sampler === s.kind
                                            ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                            : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                    ].join(" ")}
                                >
                                    {s.label || s.kind}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            n_points
                        </Label>
                        <Input
                            type="number"
                            min="1"
                            max="5000"
                            value={n_points}
                            onChange={(e) => setField("n_points", parseInt(e.target.value || "0", 10) || 0)}
                            data-testid="geometry-n-points"
                        />
                    </div>
                    {/* Phase 14.9 W2 — surface samples_per_point so
                        operators can dial trajectory variance per
                        envelope cell. Pre-14.9 this defaulted to 1
                        and was hidden; the PM "varied number of
                        trajectories" ask is now first-class UI. */}
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            samples per point
                        </Label>
                        <Input
                            type="number"
                            min="1"
                            max="100"
                            value={samples_per_point}
                            onChange={(e) => setField("samples_per_point", parseInt(e.target.value || "1", 10) || 1)}
                            data-testid="geometry-samples-per-point"
                        />
                        <p className="mt-1 text-[11px] text-ux-text-3">
                            How many engagement trajectories to run per envelope point. 1 = the
                            envelope sweep alone (default for v1). Increase to 5 / 10 / 20 to
                            sample engagement-pose variance within each cell.
                        </p>
                    </div>
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            Envelope
                        </Label>
                        <div className="space-y-2">
                            {envelopeDims.map((dim) => (
                                <div
                                    key={dim.id}
                                    className="grid grid-cols-3 items-center gap-2"
                                    data-testid={`geometry-envelope-row-${dim.id}`}
                                >
                                    <span className="text-xs text-ux-text-3">
                                        {dim.id} <span className="text-ux-text-3/60">({dim.unit})</span>
                                    </span>
                                    <Input
                                        type="number"
                                        value={envelope[dim.id]?.lo ?? ""}
                                        placeholder={`lo (${dim?.physical_bounds?.lo ?? "—"})`}
                                        onChange={(e) => setEnvelope(dim.id, "lo", e.target.value)}
                                        data-testid={`geometry-envelope-${dim.id}-lo`}
                                    />
                                    <Input
                                        type="number"
                                        value={envelope[dim.id]?.hi ?? ""}
                                        placeholder={`hi (${dim?.physical_bounds?.hi ?? "—"})`}
                                        onChange={(e) => setEnvelope(dim.id, "hi", e.target.value)}
                                        data-testid={`geometry-envelope-${dim.id}-hi`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div
                        className={[
                            "rounded-md border px-3 py-2 text-xs",
                            overCap
                                ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                                : "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3",
                        ].join(" ")}
                        data-testid="geometry-total-runs"
                        data-over-cap={overCap ? "1" : "0"}
                    >
                        Total runs: <strong className="text-ux-text-2">{totalRuns.toLocaleString()}</strong>{" "}
                        · cap {TOTAL_RUNS_CAP.toLocaleString()}{" "}
                        · {n_points.toLocaleString()} cells × {samples_per_point.toLocaleString()} trajectories/cell
                        {overCap && (
                            <span className="ml-2">— exceeds 5000-run cap; reduce n_points or samples per point.</span>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
